import { useCallback, useEffect, useState } from 'react';
import type { CodCheckoutDisplay, CodFormConfig } from '@xshop/shared';

// The query parameter that makes the one-page checkout addressable without pulling in a router.
const STEP_PARAM = 'step';
const CHECKOUT_STEP = 'checkout';

export interface CheckoutDisplayState {
  display: CodCheckoutDisplay;
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

// Reads the checkout step off the address bar, so ?step=checkout survives a reload and the back button.
function stepIsCheckout(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get(STEP_PARAM) === CHECKOUT_STEP;
}

// Rewrites only the step parameter, leaving ?tenant and ?product exactly as the visitor arrived with them.
function pushCheckoutStep(entering: boolean): void {
  const url = new URL(window.location.href);
  if (entering) url.searchParams.set(STEP_PARAM, CHECKOUT_STEP);
  else url.searchParams.delete(STEP_PARAM);
  window.history.pushState({ [STEP_PARAM]: entering ? CHECKOUT_STEP : null }, '', url);
}

// Owns where the COD form is shown and whether it is open; only ONE_PAGE spends a history entry on it.
export function useCheckoutDisplay(config: CodFormConfig | null): CheckoutDisplayState {
  const display: CodCheckoutDisplay = config?.checkoutDisplay ?? 'EMBEDDED';
  const [isOpen, setIsOpen] = useState<boolean>(stepIsCheckout);

  // The back and forward buttons move between product and checkout; the browser owns that transition.
  useEffect(() => {
    const onPopState = () => setIsOpen(stepIsCheckout());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const open = useCallback(() => {
    if (display === 'ONE_PAGE' && !stepIsCheckout()) pushCheckoutStep(true);
    setIsOpen(true);
  }, [display]);

  const close = useCallback(() => {
    if (display === 'ONE_PAGE' && stepIsCheckout()) pushCheckoutStep(false);
    setIsOpen(false);
  }, [display]);

  return { display, isOpen, open, close };
}
